import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Spin, Table, Tooltip } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCreateLead } from '../hooks/useCreateLead';
import { useUpdateLead } from '../hooks/useUpdateLead';
import { useContactOptions } from '../../../hooks/options/useContactOptions';
import { contactDisplayName } from '../../contacts/contactType';

dayjs.extend(utc);
import { useOfferingOptions } from '../../../hooks/options/useOfferingOptions';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useCampaignOptions } from '../../../hooks/options/useCampaignOptions';
import type { LeadFormValues, LeadOfferingFormValue, LeadPipelineResponse, LeadSourceDefinition } from '../types';
import { BANT_DESCRIPTIONS, BANT_SCORE_OPTIONS, isClosedCategory } from '../constants';
import { activeLeadPipelineStages, activeLeadSources } from '../leadMetadata';
import { getSuggestedTargetedAmount } from '../api/leads';

export interface LeadFormProps {
  mode: 'add' | 'edit';
  editLeadId?: string | null;
  initialValues?: Partial<LeadFormValues> | null;
  /** Display labels for edit mode when the selected entity is not in loaded options (e.g. segment scope). */
  editContactLabel?: string | null;
  editReferralContactLabel?: string | null;
  editSalesPersonLabel?: string | null;
  editAssigneeLabel?: string | null;
  pipeline?: LeadPipelineResponse;
  sources?: LeadSourceDefinition[];
  metadataLoading?: boolean;
  metadataError?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  newContactId?: string | null;
  onContactIdSet?: () => void;
  onQuickAddContact?: () => void;
}

function withSelectedOption(
  options: { value: string; label: string }[],
  selectedId: string | undefined | null,
  selectedLabel: string | undefined | null,
): { value: string; label: string }[] {
  if (!selectedId) {
    return options;
  }

  if (options.some((option) => option.value === selectedId)) {
    return options;
  }

  return [{ value: selectedId, label: selectedLabel || selectedId }, ...options];
}

function LeadForm({
  mode,
  editLeadId,
  initialValues,
  pipeline,
  sources,
  metadataLoading = false,
  metadataError = false,
  onSuccess,
  onCancel,
  newContactId,
  onContactIdSet,
  onQuickAddContact,
  editContactLabel,
  editReferralContactLabel,
  editSalesPersonLabel,
  editAssigneeLabel,
}: LeadFormProps) {
  const [form] = Form.useForm();
  const isTitleManuallyEditedRef = useRef(false);
  const lastAutoGeneratedTitleRef = useRef<string | null>(null);
  const createMutation = useCreateLead(onSuccess);
  const updateMutation = useUpdateLead(editLeadId ?? null, onSuccess);

  const [offerings, setOfferings] = useState<LeadOfferingFormValue[]>([]);
  const [pendingOfferingId, setPendingOfferingId] = useState<string | undefined>(undefined);
  const [pendingTargetedAmount, setPendingTargetedAmount] = useState<number>(0);
  const [loadingAmount, setLoadingAmount] = useState(false);

  const { data: contacts = [] } = useContactOptions();
  const { data: referralContacts = [] } = useContactOptions({ role: 0 });
  const { data: salesPersons = [] } = useContactOptions({ role: 1 });
  const { data: offeringCatalog = [] } = useOfferingOptions();
  const { data: users = [] } = useUserOptions();
  const { data: campaigns = [] } = useCampaignOptions();

  const activeStages = useMemo(() => activeLeadPipelineStages(pipeline?.stages), [pipeline?.stages]);
  const activeSources = useMemo(() => activeLeadSources(sources), [sources]);

  useEffect(() => {
    if (newContactId) {
      form.setFieldValue('contactId', newContactId);
      onContactIdSet?.();
      form
        .validateFields(['contactId'])
        .catch(() => {
        });
    }
  }, [newContactId, form, onContactIdSet]);

  useEffect(() => {
    if (mode === 'edit' && initialValues?.offerings) {
      setOfferings(initialValues.offerings.map(o => ({ offeringId: o.offeringId, targetedAmount: o.targetedAmount })));
    }
  }, [mode, initialValues]);

  const formInitialValues = useMemo(() => {
    if (mode === 'edit' && initialValues) {
      return {
        title: initialValues.title,
        description: initialValues.description,
        contactId: initialValues.contactId,
        assigneeUserId: initialValues.assigneeUserId,
        prospectingDate: initialValues.prospectingDate ? dayjs(initialValues.prospectingDate) : undefined,
        estimatedClosingDate: initialValues.estimatedClosingDate ? dayjs(initialValues.estimatedClosingDate) : undefined,
        budgetScore: initialValues.budgetScore ?? 0,
        authorityScore: initialValues.authorityScore ?? 0,
        needScore: initialValues.needScore ?? 0,
        timelineScore: initialValues.timelineScore ?? 0,
        pipelineStageId: initialValues.pipelineStageId ?? undefined,
        sourceId: initialValues.sourceId,
        campaignId: initialValues.campaignId ?? undefined,
        referralContactId: initialValues.referralContactId ?? undefined,
        salesPersonId: initialValues.salesPersonId ?? undefined,
      };
    }
    return {
      budgetScore: 0,
      authorityScore: 0,
      needScore: 0,
      timelineScore: 0,
      prospectingDate: dayjs(),
    };
  }, [mode, initialValues]);

  useEffect(() => {
    if (mode !== 'add') {
      return;
    }

    if (activeStages.length === 0) {
      return;
    }

    const current = form.getFieldValue('pipelineStageId') as number | undefined;
    const hasValidStage =
      current !== undefined &&
      activeStages.some((stage) => stage.id === current);
    if (hasValidStage) {
      return;
    }

    const firstStage = [...activeStages].sort((a, b) => a.sortOrder - b.sortOrder)[0];
    if (!firstStage) {
      return;
    }

    form.setFieldValue('pipelineStageId', firstStage.id);
  }, [mode, activeStages, form]);

  useEffect(() => {
    if (mode !== 'add' || activeSources.length === 0) {
      return;
    }

    const current = form.getFieldValue('sourceId') as number | undefined;
    const hasValidSource =
      current !== undefined &&
      current > 0 &&
      activeSources.some((source) => source.id === current);
    if (hasValidSource) {
      return;
    }

    const firstSource = [...activeSources].sort((a, b) => a.sortOrder - b.sortOrder)[0];
    form.setFieldValue('sourceId', firstSource.id);
  }, [mode, activeSources, form]);

  const stageOptions = useMemo(
    () =>
      [...activeStages]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((s) => ({ value: s.id, label: s.label })),
    [activeStages],
  );

  const sourceOptions = useMemo(
    () =>
      [...activeSources]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((s) => ({ value: s.id, label: s.label })),
    [activeSources],
  );

  const contactOptions = useMemo(
    () =>
      withSelectedOption(
        contacts.map((c) => ({
          value: c.id,
          label: c.label,
        })),
        mode === 'edit' ? initialValues?.contactId : undefined,
        editContactLabel,
      ),
    [contacts, mode, initialValues?.contactId, editContactLabel],
  );

  const referralContactOptions = useMemo(
    () =>
      withSelectedOption(
        referralContacts.map((c) => ({
          value: c.id,
          label: c.label,
        })),
        mode === 'edit' ? initialValues?.referralContactId : undefined,
        editReferralContactLabel,
      ),
    [referralContacts, mode, initialValues?.referralContactId, editReferralContactLabel],
  );

  const salesPersonOptions = useMemo(
    () =>
      withSelectedOption(
        salesPersons.map((c) => ({
          value: c.id,
          label: c.label,
        })),
        mode === 'edit' ? initialValues?.salesPersonId : undefined,
        editSalesPersonLabel,
      ),
    [salesPersons, mode, initialValues?.salesPersonId, editSalesPersonLabel],
  );

  const offeringOptionsAll = useMemo(
    () =>
      offeringCatalog.map((o) => ({
        value: o.id,
        label: o.name,
      })),
    [offeringCatalog]
  );

  const availableOfferingOptions = useMemo(() => {
    const selectedIds = new Set(offerings.map(o => o.offeringId));
    return offeringOptionsAll.filter(o => !selectedIds.has(o.value));
  }, [offeringOptionsAll, offerings]);

  const assigneeOptions = useMemo(
    () =>
      withSelectedOption(
        users.map((u) => ({
          value: u.id,
          label: u.label,
        })),
        mode === 'edit' ? initialValues?.assigneeUserId : undefined,
        editAssigneeLabel,
      ),
    [users, mode, initialValues?.assigneeUserId, editAssigneeLabel],
  );
  const campaignOptions = useMemo(
    () => campaigns.map((c) => ({ value: c.id, label: c.label })),
    [campaigns]
  );
  const watchedContactId = Form.useWatch('contactId', form) as string | undefined;
  const watchedPipelineStageId = Form.useWatch('pipelineStageId', form) as number | undefined;

  const isClosedStage = useMemo(() => {
    if (mode === 'add' || watchedPipelineStageId == null) return false;
    const stageDef = activeStages.find(s => s.id === watchedPipelineStageId);
    return stageDef ? isClosedCategory(stageDef.category) : false;
  }, [mode, watchedPipelineStageId, activeStages]);

  const totalTargetedAmount = useMemo(
    () => offerings.reduce((sum, o) => sum + (o.targetedAmount || 0), 0),
    [offerings]
  );

  const getAutoTitle = (contactId: string, firstOfferingName: string): string | null => {
    const contact = contacts.find((item) => item.id === contactId);
    const contactName = (contact ? contactDisplayName(contact) : '').trim();
    if (contactName === '' || firstOfferingName === '') {
      return null;
    }
    return `${contactName} / ${firstOfferingName} / ${dayjs().format('YYYY-MM-DD')}`;
  };

  const tryAutoSetTitle = (nextContactId?: string) => {
    if (mode !== 'add') {
      return;
    }
    if (offerings.length !== 1) {
      return;
    }

    const contactId = nextContactId ?? (form.getFieldValue('contactId') as string | undefined);
    if (!contactId) {
      return;
    }

    const offeringName = offeringCatalog.find(o => o.id === offerings[0].offeringId)?.name ?? '';
    const nextTitle = getAutoTitle(contactId, offeringName);
    if (nextTitle === null) {
      return;
    }

    const currentTitle = ((form.getFieldValue('title') as string | undefined) ?? '').trim();
    const lastAutoTitle = (lastAutoGeneratedTitleRef.current ?? '').trim();
    const canAutoUpdate =
      !isTitleManuallyEditedRef.current ||
      currentTitle === '' ||
      currentTitle === lastAutoTitle;

    if (!canAutoUpdate || currentTitle === nextTitle) {
      return;
    }

    form.setFieldValue('title', nextTitle);
    lastAutoGeneratedTitleRef.current = nextTitle;
  };

  useEffect(() => {
    if (mode !== 'add') {
      return;
    }
    tryAutoSetTitle(watchedContactId);
  }, [mode, watchedContactId, offerings, contacts, offeringCatalog, form]);

  const handleAddOffering = async () => {
    if (!pendingOfferingId) return;
    const amount = pendingTargetedAmount || 0;
    setOfferings(prev => [...prev, { offeringId: pendingOfferingId, targetedAmount: amount }]);
    setPendingOfferingId(undefined);
    setPendingTargetedAmount(0);
    form.setFields([{ name: 'offerings', errors: [] }]);
  };

  const handleRemoveOffering = (offeringId: string) => {
    setOfferings(prev => prev.filter(o => o.offeringId !== offeringId));
  };

  const handleFinish = (values: LeadFormValues & { prospectingDate?: dayjs.Dayjs; estimatedClosingDate?: dayjs.Dayjs }) => {
    if (offerings.length === 0) {
      form.setFields([{ name: 'offerings', errors: ['At least one offering is required'] }]);
      return;
    }
    form.setFields([{ name: 'offerings', errors: [] }]);
    const payload: LeadFormValues = {
      title: values.title,
      description: values.description,
      contactId: values.contactId,
      offerings,
      assigneeUserId: values.assigneeUserId,
      prospectingDate: values.prospectingDate?.utc(true).startOf('day').toISOString(),
      estimatedClosingDate: values.estimatedClosingDate?.utc(true).startOf('day').toISOString(),
      budgetScore: values.budgetScore ?? 0,
      authorityScore: values.authorityScore ?? 0,
      needScore: values.needScore ?? 0,
      timelineScore: values.timelineScore ?? 0,
      pipelineStageId: values.pipelineStageId,
      sourceId: values.sourceId,
      campaignId: values.campaignId ?? undefined,
      referralContactId: values.referralContactId ?? undefined,
      salesPersonId: values.salesPersonId ?? undefined,
    };
    if (mode === 'edit' && editLeadId) {
      updateMutation.mutate(payload, {
        onSuccess: () => {
          form.resetFields();
          isTitleManuallyEditedRef.current = false;
          lastAutoGeneratedTitleRef.current = null;
          setOfferings([]);
        },
      });
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          form.resetFields();
          isTitleManuallyEditedRef.current = false;
          lastAutoGeneratedTitleRef.current = null;
          setOfferings([]);
        },
      });
    }
  };

  const offeringTableColumns = useMemo(() => {
    const cols: any[] = [
      {
        title: 'Offering',
        dataIndex: 'offeringId',
        key: 'offeringId',
        render: (id: string) => {
          const offering = offeringCatalog.find(o => o.id === id);
          const price = offering?.effectivePrice ?? offering?.basePrice ?? 0;
          return `${offering?.name ?? id} (${price.toFixed(2)})`;
        },
      },
      {
        title: 'Targeted amount',
        dataIndex: 'targetedAmount',
        key: 'targetedAmount',
        render: (amount: number) => `${(amount || 0).toFixed(2)}`,
      },
    ];
    if (!isClosedStage) {
      cols.push({
        title: '',
        key: 'action',
        width: 50,
        render: (_: unknown, record: LeadOfferingFormValue) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveOffering(record.offeringId)}
          />
        ),
      });
    }
    return cols;
  }, [offeringCatalog, isClosedStage]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={formInitialValues}
    >
      {/* People & deal */}
      <Card title="People & deal" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="contactId"
              label="Contact"
              rules={[{ required: true, message: 'Select a contact' }]}
              required
            >
              <Space.Compact style={{ width: '100%' }}>
                <Select
                  placeholder="Select contact"
                  options={contactOptions}
                  showSearch
                  optionFilterProp="label"
                  style={{ minWidth: 120, flex: 1 }}
                  onChange={(contactId: string) => {
                    form.setFieldValue('contactId', contactId);
                    tryAutoSetTitle(contactId);
                  }}
                />
                {onQuickAddContact && (
                  <Button type="default" onClick={onQuickAddContact}>
                    Quick Add
                  </Button>
                )}
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="referralContactId" label="Referral Contact">
              <Select
                placeholder="Select referral contact (optional)"
                options={referralContactOptions}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="assigneeUserId" label="Assignee" rules={[{ required: true }]}>
              <Select placeholder="Select assignee" options={assigneeOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="salesPersonId" label="Sales Person">
              <Select
                placeholder="Select sales person (optional)"
                options={salesPersonOptions}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input
            placeholder="Lead title"
            onChange={() => {
              if (mode === 'add') {
                isTitleManuallyEditedRef.current = true;
              }
            }}
          />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="Optional notes" />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="pipelineStageId" label="Pipeline stage">
              <Select
                placeholder="Select stage"
                options={stageOptions}
                loading={metadataLoading}
                disabled={metadataError}
                notFoundContent={metadataError ? 'Failed to load stages' : undefined}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="sourceId" label="Source" rules={[{ required: true }]}>
              <Select
                options={sourceOptions}
                placeholder="Select source"
                loading={metadataLoading}
                disabled={metadataError}
                notFoundContent={metadataError ? 'Failed to load sources' : undefined}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="campaignId" label="Campaign">
              <Select
                placeholder="Select campaign (optional)"
                options={campaignOptions}
                allowClear
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Offerings */}
      <Card title="Offerings" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Form.Item name="offerings" style={{ marginBottom: 0 }}>
          <input type="hidden" />
        </Form.Item>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <Select
              placeholder="Select offering"
              value={pendingOfferingId}
              onChange={async (offeringId: string) => {
                setPendingOfferingId(offeringId);
                setPendingTargetedAmount(0);
                setLoadingAmount(true);
                try {
                  const suggested = await getSuggestedTargetedAmount([offeringId]);
                  setPendingTargetedAmount(suggested);
                } catch {
                  // ignore
                } finally {
                  setLoadingAmount(false);
                }
              }}
              options={availableOfferingOptions}
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              disabled={isClosedStage}
            />
          </Col>
          <Col xs={24} sm={8}>
            <InputNumber
              placeholder="Targeted amount"
              value={pendingTargetedAmount}
              onChange={(val) => setPendingTargetedAmount(val ?? 0)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOffering(); } }}
              min={0}
              step={0.01}
              style={{ width: '100%' }}
              suffix={loadingAmount ? <Spin size="small" /> : undefined}
              disabled={isClosedStage}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddOffering}
              disabled={!pendingOfferingId || isClosedStage}
              block
            >
              Add
            </Button>
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          <Table
            dataSource={offerings}
            columns={offeringTableColumns}
            rowKey="offeringId"
            pagination={false}
            size="small"
            locale={{ emptyText: 'No offerings added yet' }}
          />
        </div>

        <Row style={{ marginTop: 12 }}>
          <Col>
            <strong>Total targeted amount: {totalTargetedAmount.toFixed(2)}</strong>
          </Col>
        </Row>
      </Card>

      {/* Timeline */}
      <Card title="Timeline" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="prospectingDate"
              label={
                <Space size={4}>
                  Issue date
                  <Tooltip title="When this lead was opened or recorded. You can change it when editing the lead.">
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Tooltip>
                </Space>
              }
              rules={[{ required: true, message: 'Please select issue date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="estimatedClosingDate" label="Estimated closing date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* BANT */}
      <Card title="BANT scores" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="budgetScore"
              label={
                <Space>
                  Budget
                  <Tooltip title={BANT_DESCRIPTIONS.budget.slice(0, 3).join(' ')}>
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Tooltip>
                </Space>
              }
            >
              <Select options={BANT_SCORE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="authorityScore"
              label={
                <Space>
                  Authority
                  <Tooltip title={BANT_DESCRIPTIONS.authority.slice(0, 3).join(' ')}>
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Tooltip>
                </Space>
              }
            >
              <Select options={BANT_SCORE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="needScore"
              label={
                <Space>
                  Need
                  <Tooltip title={BANT_DESCRIPTIONS.need.slice(0, 3).join(' ')}>
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Tooltip>
                </Space>
              }
            >
              <Select options={BANT_SCORE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="timelineScore"
              label={
                <Space>
                  Timeline
                  <Tooltip title={BANT_DESCRIPTIONS.timeline.slice(0, 3).join(' ')}>
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Tooltip>
                </Space>
              }
            >
              <Select options={BANT_SCORE_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending}>
          {mode === 'edit' ? 'Save' : 'Create'}
        </Button>
        {onCancel && (
          <Button style={{ marginLeft: 8 }} onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Form.Item>
    </Form>
  );
}

export default LeadForm;
